// Custom types that extend Supabase types
import { Json } from './supabase';

// Additional types for better type safety
export interface BranchCounts {
    // keep legacy and expected RPC keys
    categories?: number;
    menu_items?: number;
    published_items?: number;
    available_items?: number;
    // RPC response keys used in the UI
    items_count?: number;
    main_categories_count?: number;
    sub_categories_count?: number;
}

// Type for Arabic translations
export interface ArTranslation {
    name?: string;
    description?: string;
    ingredients?: string[];
    address?: string | string[];
    [key: string]: Json | undefined;
}

// Type for theme object
export interface ThemeType {
    primaryColor?: string;
    secondaryColor?: string;
    accentColor?: string;
    backgroundColor?: string;
    textColor?: string;
    cardColor?: string;
    borderColor?: string;
    fontFamily?: string;
    titleFontFamily?: string;
    titleFontSize?: string;
    itemFontSize?: string;
    boldTitles?: boolean;
    borderRadius?: string;
    headerStyle?: string;
    cardStyle?: string;
    menuStyle?: string;
    SubcategoryStyle?: string;
    [key: string]: Json | undefined;
    isAddToCart?: boolean;
    isFeedbackEnabled?: boolean;
    isFeedbackPubliclyVisible?: boolean;
    menuSortType?: 'manual' | 'alphabetic' | 'time';
}

// Type for arrival time settings
export interface ArrivalTimeSettings {
    is_enabled: boolean;
    time_options: string[]; // Array of HH:MM times
    default_selection: number; // Index of default time option
    [key: string]: Json | undefined;
}

// Type for operating hours
export interface OperatingHoursType {
    day: string;
    opening_time: string;
    closing_time: string;
    is_closed: boolean;
    arrival_time?: ArrivalTimeSettings;
    [key: string]: Json | undefined;
}

// Type for location
export interface LocationType {
    google_maps_link?: string;
    coordinates?: string;
    place_id?: string;
    [key: string]: Json | undefined;
}

// Type for social media
export interface SocialMediaType {
    facebook?: string;
    instagram?: string;
    twitter?: string;
    whatsapp?: string;
    [key: string]: Json | undefined;
}

// Type for features
export interface Feature {
    id: string;
    name: string;
    description?: string | null;
    icon?: string | null;
    branch_id?: string | null;
    children?: Json | null;
    created_at?: string | null;
    updated_at?: string | null;
    ar?: Json | null;
}

// Type for feature children (recursive features)
export interface FeatureChild {
    id: string;
    name: string;
    description?: string;
    enabled: boolean;
    icon?: string;
    ar?: {
        name?: string;
        description?: string;
    };
    [key: string]: Json | undefined;
}

// API Response types
export interface ApiResponse<T = any> {
    success: boolean;
    message?: string;
    data?: T;
}

// Type guards for JSON data
export function isArTranslation(obj: Json | null): obj is ArTranslation {
    return typeof obj === 'object' && obj !== null && !Array.isArray(obj);
}

export function isThemeType(obj: Json | null): obj is ThemeType {
    return typeof obj === 'object' && obj !== null && !Array.isArray(obj);
}

export function isOperatingHoursArray(obj: Json | null): obj is OperatingHoursType[] {
    return Array.isArray(obj);
}

export function isLocationObject(obj: Json | null): obj is LocationType {
    return typeof obj === 'object' && obj !== null && !Array.isArray(obj);
}

export function isSocialMediaObject(obj: Json | null): obj is SocialMediaType {
    return typeof obj === 'object' && obj !== null && !Array.isArray(obj);
}

// Type helper utilities for Json field access
export function castJsonField<T>(field: Json | null | undefined): T | null {
    return field as T | null;
}

// Type guards for Feature-specific Json data
export function isFeatureArTranslation(obj: Json | null | undefined): obj is { name?: string; description?: string } {
    return typeof obj === 'object' && obj !== null && !Array.isArray(obj);
}

export function isFeatureChildrenArray(obj: Json | null | undefined): obj is FeatureChild[] {
    return Array.isArray(obj) && obj.every(item =>
        typeof item === 'object' &&
        item !== null &&
        'id' in item &&
        'name' in item &&
        'enabled' in item
    );
}

// Helper functions for Feature Json fields
export function getFeatureArName(feature: Feature, fallback: string = ''): string {
    const ar = feature.ar;
    if (isFeatureArTranslation(ar) && ar.name) {
        return ar.name;
    }
    return fallback;
}

export function getFeatureArDescription(feature: Feature, fallback: string = ''): string {
    const ar = feature.ar;
    if (isFeatureArTranslation(ar) && ar.description) {
        return ar.description;
    }
    return fallback;
}

export function getFeatureChildren(feature: Feature): FeatureChild[] {
    const children = feature.children;
    if (isFeatureChildrenArray(children)) {
        return children;
    }
    return [];
}

// Convert FeatureChild array to Json for database storage
export function featureChildrenToJson(children: FeatureChild[]): Json {
    return children as Json;
}

// Helper functions for FeatureChild Arabic content
export function getFeatureChildArName(child: FeatureChild | Omit<FeatureChild, 'enabled'>): string {
    const ar = child.ar;
    if (ar && typeof ar === 'object' && 'name' in ar && typeof ar.name === 'string') {
        return ar.name;
    }
    return '';
}

export function getFeatureChildArDescription(child: FeatureChild | Omit<FeatureChild, 'enabled'>): string {
    const ar = child.ar;
    if (ar && typeof ar === 'object' && 'description' in ar && typeof ar.description === 'string') {
        return ar.description;
    }
    return '';
}

export function updateFeatureChildAr(child: FeatureChild, updates: { name?: string; description?: string }): FeatureChild {
    const currentAr = child.ar || {};
    const newAr = typeof currentAr === 'object' && !Array.isArray(currentAr)
        ? { ...currentAr, ...updates }
        : updates;

    return {
        ...child,
        ar: newAr
    };
}